import React, { useEffect, useState } from "react";
import { ShoppingCart, Trash2, Minus, Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const RAZORPAY_SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";
const FALLBACK = "https://images.pexels.com/photos/11911071/pexels-photo-11911071.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=200&w=200";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT_URL;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function CartDrawer() {
  const { items, subtotal, count, setQty, remove, clear, open, setOpen } = useCart();
  const [stage, setStage] = useState("cart"); // cart | checkout
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "", state: "", pincode: "", notes: "" });
  const [paying, setPaying] = useState(false);
  const [payMethod, setPayMethod] = useState("razorpay"); // "razorpay" | "cod"
  const [shipping, setShipping] = useState({ fee: 0, loading: false, info: null });

  useEffect(() => {
    const pin = form.pincode.trim();
    if (pin.length !== 6) {
      setShipping({ fee: 0, loading: false, info: null });
      return;
    }
    setShipping((s) => ({ ...s, loading: true, fee: 0 }));
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/shipping/calculate", { pincode: pin, quantity: count });
        if (!cancelled) setShipping({ fee: Number(data.shipping_fee_inr) || 0, loading: false, info: data });
      } catch {
        if (!cancelled) setShipping({ fee: 70, loading: false, info: null });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [form.pincode, count]);

  const grandTotal = subtotal + (form.pincode.trim().length === 6 ? shipping.fee : 0);

  const closeAll = () => { setOpen(false); setStage("cart"); };

  const startCheckout = async () => {
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please fill name and phone");
      return;
    }
    if (payMethod === "cod" && form.pincode.trim().length !== 6) {
      toast.error("Enter a valid 6-digit pincode for Cash on Delivery");
      return;
    }
    setPaying(true);

    if (payMethod === "cod") {
      try {
        const { data } = await api.post("/orders/create-cart", {
          items: items.map((i) => ({ book_id: i.book_id, quantity: i.quantity })),
          name: form.name.trim(),
          phone: form.phone.trim(),
          address: form.address.trim(),
          city: form.city.trim(),
          state: form.state.trim(),
          pincode: form.pincode.trim(),
          notes: form.notes.trim(),
          payment_method: "cod",
        });
        toast.success(`COD order placed. Pay ₹${data.amount_inr} to the courier on delivery.`);
        clear();
        closeAll();
        setForm({ name: "", phone: "", address: "", city: "", state: "", pincode: "", notes: "" });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not place COD order");
      } finally {
        setPaying(false);
      }
      return;
    }

    const scriptOk = await loadRazorpayScript();
    if (!scriptOk || !window.Razorpay) {
      toast.error("Could not load Razorpay Checkout");
      setPaying(false);
      return;
    }

    let orderRes;
    try {
      const { data } = await api.post("/orders/create-cart", {
        items: items.map((i) => ({ book_id: i.book_id, quantity: i.quantity })),
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        pincode: form.pincode.trim(),
        notes: form.notes.trim(),
        payment_method: "razorpay",
      });
      orderRes = data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
      setPaying(false);
      return;
    }

    const options = {
      key: orderRes.razorpay_key_id,
      amount: orderRes.amount_paise,
      currency: orderRes.currency,
      name: "Chand Book Depot",
      description: `${orderRes.items_count} item(s) · ₹${orderRes.amount_inr}`,
      order_id: orderRes.razorpay_order_id,
      prefill: { name: form.name.trim(), contact: form.phone.trim() },
      notes: { internal_order_id: orderRes.order_id },
      theme: { color: "#E05A47" },
      handler: async (rzpRes) => {
        try {
          await api.post("/orders/verify", {
            order_id: orderRes.order_id,
            razorpay_order_id: rzpRes.razorpay_order_id,
            razorpay_payment_id: rzpRes.razorpay_payment_id,
            razorpay_signature: rzpRes.razorpay_signature,
          });
          toast.success("Payment received. We'll contact you shortly.");
          clear();
          closeAll();
          setForm({ name: "", phone: "", address: "", city: "", state: "", pincode: "", notes: "" });
        } catch (err) {
          toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed");
        } finally {
          setPaying(false);
        }
      },
      modal: { ondismiss: () => setPaying(false) },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setPaying(false);
      });
      rzp.open();
    } catch {
      toast.error("Could not open Razorpay checkout");
      setPaying(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col bg-[#F9F8F6]"
      >
        <SheetHeader className="px-6 py-5 border-b border-neutral-200 bg-white">
          <SheetTitle className="font-serif text-2xl flex items-center gap-2">
            <ShoppingCart size={20} className="text-[#E05A47]" /> Your Cart
          </SheetTitle>
          <SheetDescription className="text-xs">
            {count === 0 ? "No items yet" : `${count} item${count > 1 ? "s" : ""} · ₹${subtotal.toFixed(0)} subtotal`}
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center p-10 text-center">
            <div>
              <ShoppingCart size={32} className="mx-auto text-neutral-300"/>
              <p className="mt-4 text-neutral-500 text-sm">Your cart is empty.</p>
              <p className="mt-1 text-xs text-neutral-400">Browse the catalogue and add a few books.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-3">
              {stage === "cart" && items.map((i) => (
                <div key={i.book_id} data-testid={`cart-item-${i.book_id}`} className="flex gap-3 bg-white border border-neutral-200 rounded-xl p-3">
                  <img
                    src={i.has_cover ? `${API}/files/book-cover/${i.book_id}` : FALLBACK}
                    onError={(e) => { e.currentTarget.src = FALLBACK; }}
                    alt=""
                    loading="lazy"
                    className="w-14 h-16 object-cover rounded-md bg-neutral-100 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-serif text-sm text-[#0F172A] leading-tight line-clamp-2">{i.title}</div>
                    <div className="text-[11px] text-neutral-500 truncate">{i.author}</div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <div className="inline-flex items-center gap-1 border border-neutral-200 rounded-full">
                        <button
                          data-testid={`cart-dec-${i.book_id}`}
                          onClick={() => setQty(i.book_id, i.quantity - 1)}
                          className="w-7 h-7 flex items-center justify-center text-neutral-600 hover:text-[#E05A47]"
                          aria-label="Decrease"
                        >
                          <Minus size={12}/>
                        </button>
                        <span className="w-6 text-center text-xs font-medium" data-testid={`cart-qty-${i.book_id}`}>{i.quantity}</span>
                        <button
                          data-testid={`cart-inc-${i.book_id}`}
                          onClick={() => setQty(i.book_id, i.quantity + 1)}
                          className="w-7 h-7 flex items-center justify-center text-neutral-600 hover:text-[#E05A47]"
                          aria-label="Increase"
                        >
                          <Plus size={12}/>
                        </button>
                      </div>
                      <div className="text-sm text-[#E05A47] font-medium">₹ {(i.price * i.quantity).toFixed(0)}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`cart-remove-${i.book_id}`}
                    onClick={() => remove(i.book_id)}
                    className="text-neutral-400 hover:text-[#E05A47] p-1"
                    aria-label="Remove"
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}

              {stage === "checkout" && (
                <div data-testid="cart-checkout-form" className="space-y-4">
                  <button
                    onClick={() => setStage("cart")}
                    data-testid="cart-back-btn"
                    className="text-xs text-neutral-500 hover:text-[#E05A47] uppercase tracking-[0.2em]"
                  >
                    ← Back to cart
                  </button>
                  <FieldRow label="Your name">
                    <Input data-testid="cart-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required/>
                  </FieldRow>
                  <FieldRow label="Phone">
                    <Input data-testid="cart-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" required/>
                  </FieldRow>
                  <FieldRow label="Delivery address (blank = pickup)">
                    <Textarea data-testid="cart-address-input" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}/>
                  </FieldRow>
                  <div className="grid grid-cols-3 gap-2">
                    <FieldRow label="Pincode">
                      <Input data-testid="cart-pincode-input" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/[^0-9]/g, "").slice(0,6) })} placeholder="110085"/>
                    </FieldRow>
                    <FieldRow label="City">
                      <Input data-testid="cart-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Delhi"/>
                    </FieldRow>
                    <FieldRow label="State">
                      <Input data-testid="cart-state-input" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Delhi"/>
                    </FieldRow>
                  </div>
                  <p className="text-[11px] text-neutral-500 -mt-1">Fill pincode for home delivery via Shiprocket. Leave blank for pickup at shop.</p>
                  <FieldRow label="Notes (optional)">
                    <Input data-testid="cart-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
                  </FieldRow>
                  <div data-testid="cart-payment-method" className="space-y-2">
                    <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Payment method</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        data-testid="cart-pay-razorpay"
                        onClick={() => setPayMethod("razorpay")}
                        className={`h-10 rounded-full text-xs font-medium border transition-colors ${payMethod === "razorpay" ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#0F172A]"}`}
                        style={{ transitionProperty: "background-color, color, border-color" }}
                      >
                        Pay online
                      </button>
                      <button
                        type="button"
                        data-testid="cart-pay-cod"
                        onClick={() => setPayMethod("cod")}
                        className={`h-10 rounded-full text-xs font-medium border transition-colors ${payMethod === "cod" ? "bg-[#4A6741] text-white border-[#4A6741]" : "bg-white text-neutral-700 border-neutral-200 hover:border-[#4A6741]"}`}
                        style={{ transitionProperty: "background-color, color, border-color" }}
                      >
                        Cash on Delivery
                      </button>
                    </div>
                    {payMethod === "cod" && (
                      <p className="text-[11px] text-neutral-500">Requires a valid 6-digit delivery pincode. Pay the courier in cash on arrival.</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-5 border-t border-neutral-200 bg-white space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">Subtotal</span>
                <span className="font-serif text-2xl text-[#0F172A]">₹ {subtotal.toFixed(0)}</span>
              </div>
              {stage === "checkout" && (
                <div data-testid="cart-shipping-summary" className="space-y-1 text-sm">
                  <div className="flex items-center justify-between text-neutral-600">
                    <span data-testid="cart-shipping-label">
                      Shipping{shipping.info?.courier_name ? ` · ${shipping.info.courier_name}` : ""}
                    </span>
                    <span data-testid="cart-shipping-fee">
                      {form.pincode.trim().length !== 6
                        ? "Free (shop pickup)"
                        : shipping.loading
                          ? "Calculating…"
                          : `₹ ${shipping.fee.toFixed(0)}`}
                    </span>
                  </div>
                  {form.pincode.trim().length === 6 && !shipping.loading && shipping.info?.source === "fallback" && (
                    <div className="text-[11px] text-neutral-400">Standard flat rate applied</div>
                  )}
                  <div className="flex items-center justify-between border-t border-neutral-200 pt-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-neutral-500">Total</span>
                    <span data-testid="cart-grand-total" className="font-serif text-2xl text-[#E05A47]">₹ {grandTotal.toFixed(0)}</span>
                  </div>
                </div>
              )}
              {stage === "cart" ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    data-testid="cart-clear-btn"
                    onClick={clear}
                    className="flex-1 rounded-full"
                  >
                    Clear
                  </Button>
                  <Button
                    data-testid="cart-checkout-btn"
                    onClick={() => setStage("checkout")}
                    className="flex-1 rounded-full bg-[#0F172A] hover:bg-[#E05A47] text-white transition-colors"
                    style={{ transitionProperty: "background-color" }}
                  >
                    Checkout
                  </Button>
                </div>
              ) : (
                <Button
                  data-testid="cart-pay-btn"
                  onClick={startCheckout}
                  disabled={paying}
                  className={`w-full h-11 rounded-full text-white transition-colors ${payMethod === "cod" ? "bg-[#4A6741] hover:bg-[#3a5233]" : "bg-[#E05A47] hover:bg-[#c94a38]"}`}
                  style={{ transitionProperty: "background-color" }}
                >
                  {paying
                    ? <><Loader2 size={14} className="mr-2 animate-spin"/>{payMethod === "cod" ? "Placing…" : "Opening…"}</>
                    : payMethod === "cod"
                      ? `Place COD order · ₹${grandTotal.toFixed(0)}`
                      : `Pay ₹${grandTotal.toFixed(0)}`}
                </Button>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
